import {Component, inject, Input, OnInit} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {EstablecimientosService} from './establecimientos.service';
import {ErrorService} from '../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {Establecimiento} from '../clientes/clientes.interface';
import {DeleteTrigger} from '../../../../../shared/components/ui/delete-trigger/delete-trigger';
import {PageHeaderComponent} from '../../../../../shared/components/common/page-header/page-header.component';
import {ActivatedRoute, Router} from '@angular/router';
import {ButtonComponent} from '@shared/components/ui/button/button.component';
import {DatePipe, DecimalPipe} from '@angular/common';
import {DialogService} from '@shared/services/dialog.service';
import {EstablecimientoModal} from './establecimiento-modal/establecimiento-modal';
import {
  EstablecimientosGrilla,
  errorFilaEstablecimiento,
  FilaEstablecimiento,
  filaEstablecimientoConDatos,
  payloadFilaEstablecimiento,
} from './establecimientos-grilla/establecimientos-grilla';
import {
  DataTable,
  TableColumn,
  TableCellDirective,
  Pagination,
  SearchInput,
  FilterSelect,
  FilterOption,
  FiltersContainer
} from '../../../../../shared/components/ui/list-components';

@Component({
  selector: 'app-establecimientos',
  imports: [
    HasPermissionDirective,
    DeleteTrigger,
    PageHeaderComponent,
    DataTable,
    TableCellDirective,
    Pagination,
    // Filtros comentados en el template
    // SearchInput,
    // FilterSelect,
    // FiltersContainer,
    // ButtonComponent,  (solo lo usaba la grilla en línea)
    // EstablecimientosGrilla,
    DatePipe,
    DecimalPipe,
  ],
  templateUrl: './establecimientos.html',
  styles: ``
})
export class Establecimientos implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  @Input() clienteId!: number;
  loading = false;
  deleting = false;
  deletingIndex: number | null = null;
  establecimientos: Establecimiento[] = [];

  // Alta en línea: filas nuevas sobre el listado, sin ir a otra página
  nuevos: FilaEstablecimiento[] | null = null;
  guardandoNuevos = false;
  mostrarErroresNuevos = false;

  // Paginación. En la ficha del cliente se listan todos sus establecimientos de una vez.
  currentPage = 1;
  lastPage = 1;
  perPage = 100;
  total = 0;

  // Filtros
  searchTerm = '';
  selectedObjetivo: string | number = '';

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'nombre', header: 'Nombre' },
    { key: 'direccion', header: 'Zona' },
    { key: 'km', header: 'km' },
    { key: 'animales', header: 'Animales' },
    { key: 'proximo', header: 'Próx. agendamiento' },
    { key: 'acciones', header: '', width: '1%' }
  ];

  // Opciones de filtro para objetivos
  objetivosOptions: FilterOption[] = [
    { value: 'E', label: 'Producción de embriones' },
    { value: 'P', label: 'Preñez' },
    { value: 'X', label: 'Producción de Embriones/Preñez' }
  ];

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private establecimientosService = inject(EstablecimientosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.getEstablecimientos();
  }

  getEstablecimientos(page: number = 1): void {
    this.loading = true;

    // Construir filtros para enviar al backend
    const filters = {
      cli_cliente_id: this.clienteId,
      page: page,
      per_page: this.perPage,
      search: this.searchTerm || undefined,
      objetivo: this.selectedObjetivo ? this.selectedObjetivo.toString() : undefined
    };

    this.establecimientosService.index(filters).subscribe({
      next: (response) => {
        if (response.status) {
          this.establecimientos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  // Handlers de filtros
  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  onObjetivoChange(value: string | number): void {
    this.selectedObjetivo = value;
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  get hasActiveFilters(): boolean {
    return !!this.searchTerm || !!this.selectedObjetivo;
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedObjetivo = '';
    this.currentPage = 1;
    this.getEstablecimientos();
  }

  // Handlers de paginación
  onPageChange(page: number): void {
    this.getEstablecimientos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getEstablecimientos(1);
  }

  // Handler de click en fila
  onRowClick(event: { item: Establecimiento; index: number }): void {
    this.view(event.item);
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.establecimientosService.destroy(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Establecimiento eliminado correctamente');
          this.getEstablecimientos(this.currentPage);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }

  create(): void {
    // Antes: página aparte que al guardar llevaba al detalle del establecimiento.
    // this.router.navigate(['establecimientos/new'], {relativeTo: this.route});
    // Después: filas en línea sobre el listado (grilla). Desde 2026-10-05 el alta
    // es el mismo modal del establecimiento, vacío.
    // this.nuevos ??= [];
    // this.mostrarErroresNuevos = false;
    this.dialogService.openForm(EstablecimientoModal, {size: 'xl', data: {clienteId: this.clienteId}})
      .afterClosed()
      .subscribe((creado) => {
        if (creado) this.getEstablecimientos(this.currentPage);
      });
  }

  cancelarNuevos(): void {
    this.nuevos = null;
  }

  /** Guarda las filas una por una; las que ya se guardaron salen de la grilla aunque otra falle. */
  guardarNuevos(): void {
    const filas = (this.nuevos ?? []).filter(filaEstablecimientoConDatos);
    if (filas.some(fila => errorFilaEstablecimiento(fila))) {
      this.mostrarErroresNuevos = true;
      return;
    }
    if (!filas.length) {
      this.nuevos = null;
      return;
    }

    this.guardandoNuevos = true;
    const siguiente = (i: number): void => {
      if (i === filas.length) {
        this.guardandoNuevos = false;
        this.nuevos = null;
        this.snackbarService.showSnackBar(filas.length === 1 ? 'Establecimiento creado correctamente' : 'Establecimientos creados correctamente');
        this.getEstablecimientos(this.currentPage);
        return;
      }
      this.establecimientosService.store({cli_cliente_id: this.clienteId, ...payloadFilaEstablecimiento(filas[i])}).subscribe({
        next: () => {
          this.nuevos?.splice(this.nuevos.indexOf(filas[i]), 1);
          siguiente(i + 1);
        },
        error: (error) => {
          this.guardandoNuevos = false;
          this.getEstablecimientos(this.currentPage);
          this.errorService.handleError(error);
        }
      });
    };
    siguiente(0);
  }

  // Antes iba a la página del establecimiento; ahora se abre en un modal sin salir de la ficha.
  // view(id: number): void {
  //   this.router.navigate(['establecimientos/', id], {relativeTo: this.route});
  // }
  view(establecimiento: Establecimiento): void {
    this.dialogService.openForm(EstablecimientoModal, {size: 'xl', data: {clienteId: this.clienteId, establecimiento}})
      .afterClosed()
      .subscribe((cambio) => {
        if (cambio) this.getEstablecimientos(this.currentPage);
      });
  }
}
